import { validateFileType } from '@repo/studio-utils-mime-type/validate-file-type'

type FileType =
  | 'jpg'
  | 'jpeg'
  | 'png'
  | 'tiff'
  | 'gif'
  | 'pdf'
  | 'webp'
  | 'heif'
  | 'heic'
  | 'raw'
  | 'ico'
  | 'eps'
  | 'svg'
  | 'hdr'
  | 'bmp'
  | 'dds'
  | 'cur'
  | 'psd'
  | 'tga'
  | 'avif'
  | 'rgd'

/**
 * @param extensions a set of file extensions like 'jpg', 'png', etc.
 * @returns a string that can be used in the `accept` attribute of an input element
 */
export const getFileExtension = (fileName: string): string => {
  return fileName.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2)
}

export const getInputAcceptFromExtensions = (extensions?: Set<string>): string => {
  return extensions ? [...extensions].map(ext => `.${ext}`).join(',') : '*/*'
}

// Performance-wise it's better if this is swapped (Record<string, FileType>
// However we want to keep the type safety to ensure that if we add new file types,
// we will also add the mime types here
const mimeMap: Record<FileType, string[]> = {
  svg: ['image/svg+xml'],
  png: ['image/png'],
  ico: ['image/x-icon', 'image/vnd.microsoft.icon'],
  jpg: ['image/jpg', 'image/jpeg'],
  jpeg: ['image/jpg', 'image/jpeg'],
  webp: ['image/webp'],
  hdr: ['image/vnd.radiance'],
  bmp: ['image/bmp'],
  dds: ['image/dds'],
  gif: ['image/gif'],
  cur: ['image/x-win-bitmap'],
  psd: ['image/psd', 'image/x-photoshop'],
  tiff: ['image/tiff'],
  tga: ['image/x-tga'],
  avif: ['image/avif'],
  rgd: ['image/rgd'],
  heif: ['image/heif'],
  heic: ['image/heic'],
  raw: ['image/raw'],
  pdf: ['application/pdf'],
  eps: ['application/eps', 'application/postscript', 'image/eps'],
}
export const getFileExtensionFromMimeType = (mimeType: string): string | null => {
  const normalizedMimeType = mimeType.trim().toLowerCase()

  for (const [fileType, mimeTypes] of Object.entries(mimeMap)) {
    if (mimeTypes.includes(normalizedMimeType)) {
      return fileType
    }
  }

  return null
}

export function getFileExtensionFromUrl(url: string) {
  const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/)
  return match ? match[1]?.toLowerCase() : null
}

export function getSupportedMimeTypes(supportedExtensions: Set<string>): Set<string> {
  const mimeTypes = new Set<string>()

  for (const extension of supportedExtensions) {
    const mimes = (mimeMap as Record<string, string[]>)[extension] || []
    mimes.forEach(mime => mimeTypes.add(mime))
  }

  return mimeTypes
}

export async function getFilteredFiles(
  files: File[],
  allowedExtensions?: Set<string>,
  allowedMimeTypes?: Set<string>,
): Promise<{ filteredFiles: File[]; rejectedFiles: File[] }> {
  if (!allowedExtensions && !allowedMimeTypes) {
    return { filteredFiles: files, rejectedFiles: [] }
  }

  const validationResults = await Promise.all(
    files.map(async file => {
      if (allowedExtensions && allowedMimeTypes) {
        const isValid = await validateFileType(file, allowedExtensions, allowedMimeTypes)
        return { file, isValid }
      }

      // Extension-only: check the file name suffix.
      if (allowedExtensions) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase()
        const isValid = Boolean(fileExtension && allowedExtensions.has(fileExtension))
        return { file, isValid }
      }

      // MIME-only (e.g. audio section): trust the browser-reported file.type.
      // Magic-number detection doesn't cover audio/video formats so we rely on
      // the OS/browser assignment, which is accurate for files selected via the
      // native picker or drag-and-drop.
      const mimeType = file.type.toLowerCase().split(';')[0].trim()
      const isValid = Boolean(mimeType && allowedMimeTypes?.has(mimeType))
      return { file, isValid }
    }),
  )

  const filteredFiles = validationResults
    .filter(result => result.isValid)
    .map(result => result.file)
  const rejectedFiles = validationResults
    .filter(result => !result.isValid)
    .map(result => result.file)

  return { filteredFiles, rejectedFiles }
}

/**
 * Get the first registered MIME type for a given file extension.
 * @param extension file extension without the leading dot (e.g. "png")
 * @returns a MIME type string like "image/png" if known, otherwise undefined
 */
export function getMimeTypeFromExtension(extension: string): string | undefined {
  const ext = extension.trim().toLowerCase() as FileType | string
  const mimeTypes = (mimeMap as Record<string, string[]>)[ext]
  return mimeTypes ? mimeTypes[0] : undefined
}

/** Undefined when the url carries no recognisable extension, e.g. an object url. */
export function getMimeTypeFromUrl(url: string): string | undefined {
  const extension = getFileExtensionFromUrl(url)
  return extension ? getMimeTypeFromExtension(extension) : undefined
}
